/**
 * Custom hooks per Document Versioning
 * Gestisce versioni multiple dello stesso documento
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPostFormData } from '@/lib/api-client';
import type {
  DocumentVersionsListResponse,
  DocumentVersionResponse,
  VersionCountResponse,
  DocumentResponse,
} from '@/types/api/documents';

// Query keys per versioning
export const DOCUMENT_VERSIONS_QUERY_KEYS = {
  all: ['document-versions'] as const,
  versions: (documentId: string) => [...DOCUMENT_VERSIONS_QUERY_KEYS.all, documentId, 'versions'] as const,
  versionCount: (documentId: string) => [...DOCUMENT_VERSIONS_QUERY_KEYS.all, documentId, 'count'] as const,
  latestVersion: (documentId: string) => [...DOCUMENT_VERSIONS_QUERY_KEYS.all, documentId, 'latest'] as const,
};

/**
 * Hook per ottenere tutte le versioni di un documento
 * Restituisce la lista completa ordinata per versione (più recente prima)
 */
export function useDocumentVersions(documentId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.versions(documentId),
    queryFn: async (): Promise<DocumentVersionsListResponse> => {
      return await apiGet<DocumentVersionsListResponse>(`/documents/${documentId}/versions`);
    },
    enabled: options?.enabled !== false && !!documentId,
    staleTime: 1000 * 60 * 5, // 5 minuti - le versioni non cambiano spesso
  });
}

/**
 * Hook per ottenere il conteggio versioni (per indicatore UI)
 * Ottimizzato per essere leggero e usabile in liste
 */
export function useDocumentVersionCount(documentId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.versionCount(documentId),
    queryFn: async (): Promise<VersionCountResponse> => {
      return await apiGet<VersionCountResponse>(`/documents/${documentId}/version-count`);
    },
    enabled: options?.enabled !== false && !!documentId,
    staleTime: 1000 * 60 * 10, // 10 minuti - conteggio cambia raramente
  });
}

/**
 * Hook per ottenere l'ultima versione di un documento
 */
export function useDocumentLatestVersion(documentId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.latestVersion(documentId),
    queryFn: async (): Promise<DocumentVersionResponse> => {
      return await apiGet<DocumentVersionResponse>(`/documents/${documentId}/latest-version`);
    },
    enabled: options?.enabled !== false && !!documentId,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook per creare una nuova versione di un documento
 * Carica un nuovo file come versione successiva
 */
export function useCreateDocumentVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      file
    }: {
      documentId: string;
      file: File;
    }): Promise<DocumentResponse> => {
      const formData = new FormData();
      formData.append('file', file);

      return await apiPostFormData<DocumentResponse>(
        `/documents/${documentId}/create-version`,
        formData
      );
    },
    onSuccess: (_data, variables) => {
      // Invalida tutte le query relative alle versioni
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.versions(variables.documentId)
      });
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.versionCount(variables.documentId)
      });
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.latestVersion(variables.documentId)
      });
      // Invalida anche la lista documenti generale
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

/**
 * Hook per prefetch delle versioni (utile per hover su indicatore)
 */
export function usePrefetchDocumentVersions() {
  const queryClient = useQueryClient();

  return (documentId: string) => {
    queryClient.prefetchQuery({
      queryKey: DOCUMENT_VERSIONS_QUERY_KEYS.versions(documentId),
      queryFn: async (): Promise<DocumentVersionsListResponse> => {
        return await apiGet<DocumentVersionsListResponse>(`/documents/${documentId}/versions`);
      },
      staleTime: 1000 * 60 * 5,
    });
  };
}
